import * as React from 'react';

import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { Button, Col, Row, Typography } from 'antd';

import { L } from '@lib';

import { useStore } from '@stores';

import { SmallLogo } from '@components';
import { usePageTitle, useRoleAccess, useView } from '@hooks';
import { lazy, Suspense } from 'react';
import { Breadcrumbs } from './Breadcrumbs';
import './header-responsive.css';

const { Text } = Typography;

declare const abp: any;

export interface IHeaderProps {
  collapsed?: boolean;
  toggle?: () => void;
}

const BookingDrawer = lazy(() => import('../Drawer/BookingDrawer/BookingDrawer.container'));
const QuickPayDrawer = lazy(() => import('../Drawer/QuickPayDrawer/QuickPayDrawer.container'));

export const Header: React.FC<IHeaderProps> = ({ collapsed, toggle }) => {
  const { isMobile } = useView();
  const { accountStore } = useStore();
  const { isTechnician, header: access } = useRoleAccess();

  const { title: pageTitle, headerActions, extraHeaderActions, headerRows } = usePageTitle();
  const [showDrawer, setShowDrawer] = React.useState(false);
  const [showQuickPay, setShowQuickPay] = React.useState(false);

  const handleBackToImpersonator = React.useCallback(async () => {
    await accountStore.backToImpersonator().then(async (response) => {
      await accountStore.impersonateLogin(response.impersonationToken).then(() => {
        window.location.href = '/garages';
      });
    });
  }, [accountStore]);

  return (
    <Row
      className="header-container"
      justify="space-between"
      wrap={isMobile}
      style={{ paddingRight: isMobile ? '12px' : '24px', paddingTop: 8, lineHeight: 1 }}
    >
      <>
        {isMobile ? (
          headerRows === 2 ? (
            <div style={{ width: '100%' }}>
              {/* Row 1: hamburger, title, extra actions, new booking */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  type="text"
                  onClick={toggle}
                  style={{ marginLeft: 4, marginRight: 4, flexShrink: 0 }}
                  icon={
                    collapsed ? (
                      <MenuUnfoldOutlined style={{ fontSize: '130%' }} />
                    ) : (
                      <MenuFoldOutlined style={{ fontSize: '130%' }} />
                    )
                  }
                />
                <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {pageTitle && pageTitle.trim() ? (
                    <Text strong ellipsis style={{ fontSize: 15, margin: 0, lineHeight: '20px' }}>
                      {pageTitle}
                    </Text>
                  ) : (
                    <Breadcrumbs />
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  {extraHeaderActions}
                  {(access.newBooking || access.quickPay) && (
                    <Suspense fallback={null}>
                      {/* Icon only, like the + next to it: the row has no room for a word. */}
                      {access.quickPay && (
                        <Button
                          type="primary"
                          ghost
                          icon={<WalletOutlined />}
                          aria-label={L('QuickPay')}
                          title={L('QuickPay')}
                          onClick={() => setShowQuickPay(true)}
                        />
                      )}
                      {access.newBooking && (
                        <BookingDrawer
                          mode="booking"
                          showDrawer={showDrawer}
                          setShowDrawer={setShowDrawer}
                        />
                      )}
                      {access.quickPay && (
                        <QuickPayDrawer
                          showDrawer={showQuickPay}
                          setShowDrawer={setShowQuickPay}
                        />
                      )}
                    </Suspense>
                  )}
                </div>
              </div>
              {/* Row 2: page-specific actions */}
              {headerActions && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px 2px' }}>
                  {headerActions}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Single row: hamburger, title, all actions */}
              <Col style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                <Button
                  type="text"
                  onClick={toggle}
                  style={{ marginLeft: 4, marginRight: 4 }}
                  icon={
                    collapsed ? (
                      <MenuUnfoldOutlined style={{ fontSize: '130%' }} />
                    ) : (
                      <MenuFoldOutlined style={{ fontSize: '130%' }} />
                    )
                  }
                />
              </Col>
              <Col style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                {pageTitle && pageTitle.trim() ? (
                  <Text strong ellipsis style={{ fontSize: 15, margin: 0, lineHeight: '20px' }}>
                    {pageTitle}
                  </Text>
                ) : (
                  <Breadcrumbs />
                )}
              </Col>
              <Col style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                {headerActions}
                {extraHeaderActions}
                {(access.newBooking || access.quickPay) && (
                  <Suspense fallback={null}>
                    {access.quickPay && (
                      <Button
                        type="primary"
                        ghost
                        icon={<WalletOutlined />}
                        aria-label={L('QuickPay')}
                        title={L('QuickPay')}
                        onClick={() => setShowQuickPay(true)}
                      />
                    )}
                    {access.newBooking && (
                      <BookingDrawer
                        mode="booking"
                        showDrawer={showDrawer}
                        setShowDrawer={setShowDrawer}
                      />
                    )}
                    {access.quickPay && (
                      <QuickPayDrawer
                        showDrawer={showQuickPay}
                        setShowDrawer={setShowQuickPay}
                      />
                    )}
                  </Suspense>
                )}
              </Col>
            </>
          )
        ) : (
          <>
            {/* Desktop: original layout */}
            {/* The gutter the hamburger used to provide: without it the title sat flush against
                the window edge. */}
            <Col style={{ display: 'flex', alignItems: 'center', paddingLeft: 16 }}>
              <Row>
                {/* The sider is collapsed from its own logo now, so there is no control here; a
                    technician has no sider at all, so the logo stands in for it. */}
                {isTechnician && (
                  <Col style={{ padding: '4px ' }}>
                    <SmallLogo />
                  </Col>
                )}
                <Col
                  style={{
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  {pageTitle ? (
                    <Text strong ellipsis style={{ fontSize: 15, maxWidth: 400 }}>
                      {pageTitle}
                    </Text>
                  ) : (
                    <Breadcrumbs />
                  )}
                </Col>
              </Row>
            </Col>
            {abp.session.impersonatorUserId && (
              <Col>
                <Button
                  size="large"
                  style={{ marginRight: 8 }}
                  type="primary"
                  onClick={handleBackToImpersonator}
                >
                  {L('Back to impersonator')}
                </Button>
              </Col>
            )}
            <Col
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {extraHeaderActions}
              {headerActions}
              {(access.newBooking || access.quickPay) && (
                <Suspense fallback={null}>
                  {access.quickPay && (
                    <Button type="primary" ghost onClick={() => setShowQuickPay(true)}>
                      {L('QuickPay')}
                    </Button>
                  )}
                  {access.newBooking && (
                    <BookingDrawer
                      mode="booking"
                      showDrawer={showDrawer}
                      setShowDrawer={setShowDrawer}
                    />
                  )}
                  {access.quickPay && (
                    <QuickPayDrawer
                      showDrawer={showQuickPay}
                      setShowDrawer={setShowQuickPay}
                    />
                  )}
                </Suspense>
              )}
            </Col>
          </>
        )}
      </>
    </Row>
  );
};

export default Header;
